import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import {
  getPurchaseLineFeatures,
  type PurchaseLineFeatures,
} from "../../api/documents";
import {
  checkPurchaseMatching,
  updateGoodsInvoice,
  type PurchaseMatchingCheck,
} from "../../api/purchaseMatching";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type Props = {
  documentId: string;
  companyId: string;
  goodsInvoice: boolean;
  onGoodsInvoiceChanged?: () => void;
};

export default function DocumentPurchaseMatchingIntro({
  documentId,
  companyId,
  goodsInvoice,
  onGoodsInvoiceChanged,
}: Props) {
  const { t } = useAppTranslation("common");

  const [features, setFeatures] = useState<PurchaseLineFeatures | null>(null);
  const [matchingCheck, setMatchingCheck] =
    useState<PurchaseMatchingCheck | null>(null);
  const [localGoodsInvoice, setLocalGoodsInvoice] = useState(goodsInvoice);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLocalGoodsInvoice(goodsInvoice);
    setDismissed(false);
  }, [documentId, goodsInvoice]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setError(null);
      setMatchingCheck(null);

      try {
        const featureFlags = await getPurchaseLineFeatures(companyId);

        if (!active) {
          return;
        }

        setFeatures(featureFlags);

        if (!featureFlags.usePurchOrderMatching || localGoodsInvoice) {
          return;
        }

        setLoading(true);

        const result = await checkPurchaseMatching(companyId, documentId);

        if (!active) {
          return;
        }

        setMatchingCheck(result);
      } catch (e) {
        console.error(e);

        if (!active) {
          return;
        }

        setError(
          e instanceof Error
            ? e.message
            : t("Nie udało się sprawdzić zamówień i przyjęć dla faktury.")
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, [companyId, documentId, localGoodsInvoice, t]);

  const confirmGoodsInvoice = async () => {
    try {
      setSaving(true);
      setError(null);

      const result = await updateGoodsInvoice(
        companyId,
        documentId,
        true
      );

      setLocalGoodsInvoice(result.goodsInvoice);
      setMatchingCheck(null);
      setDismissed(false);
      onGoodsInvoiceChanged?.();
    } catch (e) {
      console.error(e);
      setError(
        e instanceof Error
          ? e.message
          : t("Nie udało się zaktualizować typu faktury.")
      );
    } finally {
      setSaving(false);
    }
  };

  if (!features?.usePurchOrderMatching) {
    return null;
  }

  if (localGoodsInvoice) {
    return (
      <Alert severity="success" sx={{ mt: 2, mb: 1.5 }}>
        <Typography variant="body2" fontWeight={700}>
          {t("Dopasowanie ZZ/PZ jest aktywne")}
        </Typography>
        <Typography variant="body2">
          {t(
            "Dopasowanie ZZ/PZ jest aktywne. Zatwierdzenie dokumentu będzie wymagało kompletnego dopasowania każdego wiersza faktury."
          )}
        </Typography>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 2, mb: 1.5 }}>
        {error}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          mt: 2,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          {t("Sprawdzanie otwartych ZZ/PZ...")}
        </Typography>
      </Box>
    );
  }

  if (
    dismissed ||
    !matchingCheck ||
    !matchingCheck.suggestGoodsInvoice
  ) {
    return null;
  }

  return (
    <Alert severity="info" sx={{ mt: 2, mb: 1.5 }}>
      <Stack spacing={1.25}>
        <Box>
          <Typography variant="body2" fontWeight={700}>
            {t("Znaleziono otwarte zamówienia lub przyjęcia dla tego dostawcy.")}
          </Typography>
          <Typography variant="body2">
            {t(
              "Otwarte wiersze ZZ: {{orders}}. Nierozliczone wiersze PZ: {{receipts}}.",
              {
                orders: matchingCheck.openOrderLineCount,
                receipts: matchingCheck.openReceiptLineCount,
              }
            )}
          </Typography>
          <Typography variant="body2">
            {t(
              "Czy ten dokument jest fakturą towarową i powinien zostać dopasowany do ZZ/PZ?"
            )}
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={confirmGoodsInvoice}
            disabled={saving}
          >
            {saving
              ? t("Zapisywanie...")
              : t("Tak, oznacz jako fakturę towarową")}
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => setDismissed(true)}
            disabled={saving}
          >
            {t("Nie, pozostaw bez zmian")}
          </Button>
        </Stack>
      </Stack>
    </Alert>
  );
}
